import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Ticket, 
  Kanban,
  Users,
  Settings,
  LogOut,
  User,
  Menu,
  X,
  ChevronRight,
  Building2,
  Building,
  FolderKanban,
  FileText,
  Check,
} from 'lucide-react';
import { roleConfig } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { UserAvatar } from '../components/UserAvatar';
import CompanySwitcher from '../components/CompanySwitcher';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'jefatura', 'solucionador', 'solicitante'] },
  { to: '/tickets', label: 'Tickets', icon: Ticket, roles: ['admin', 'jefatura', 'solucionador', 'solicitante'] },
  { to: '/kanban', label: 'Kanban', icon: Kanban, roles: ['admin', 'jefatura', 'solucionador'] },
  { to: '/projects', label: 'Proyectos', icon: FolderKanban, roles: ['admin', 'jefatura', 'solucionador'] },
  { to: '/solicitudes', label: 'Solicitudes', icon: FileText, roles: ['admin', 'jefatura'] },
  { to: '/admin/users', label: 'Usuarios', icon: Users, roles: ['admin', 'jefatura'] },
  { to: '/admin/gerencias', label: 'Gerencias/Areas', icon: Building2, roles: ['admin', 'jefatura'] },
  { to: '/empresas', label: 'Empresas', icon: Building, roles: ['admin'] },
];

export default function MainLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="main-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
              {sidebarOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_velocity-ops-1/artifacts/3sjrhvi5_Logo%20NextPlan.png" 
              alt="NextPlan" 
              className="h-8 object-contain"
            />
          </div>
          <UserMenu user={user} onLogout={handleLogout} />
        </div>
      </header>

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 z-40 h-full w-64 bg-white border-r border-gray-200 shadow-sm
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-[60px] px-4 border-b border-gray-100 flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_velocity-ops-1/artifacts/3sjrhvi5_Logo%20NextPlan.png" 
              alt="NextPlan" 
              className="max-h-9 w-auto object-contain"
            />
          </div>

          {/* Company Switcher (admin only) */}
          {user?.role === 'admin' && (
            <div className="p-3 border-b border-gray-100">
              <CompanySwitcher />
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive 
                    ? 'bg-[#7FD02E]/10 text-[#7FD02E] border border-[#7FD02E]/20 font-medium' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User Info (Desktop) */}
          <div className="hidden lg:block p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <UserAvatar user={user} size="lg" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-gray-800 break-words leading-tight">
                  {user?.first_name} {user?.last_name}
                </p>
                <Badge className={`${roleConfig[user?.role]?.color} text-xs mt-1`}>
                  {roleConfig[user?.role]?.label}
                </Badge>
              </div>
              <UserMenu user={user} onLogout={handleLogout} />
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-30 bg-black/20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen flex flex-col">
        {/* Desktop Top Bar - Company context */}
        <div className="hidden lg:flex sticky top-0 z-30 h-[60px] bg-white/95 backdrop-blur border-b border-gray-100 px-8 items-center justify-end gap-3">
          {user?.company ? (
            <div className="flex items-center gap-3">
              <div className="text-right leading-tight">
                <p className="text-xs text-gray-500">
                  {user?.role === 'admin' ? 'Trabajando como' : 'Empresa'}
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {user.company.name}
                </p>
              </div>
              {user.company.logo_url ? (
                <img
                  src={user.company.logo_url}
                  alt={user.company.name}
                  className="w-9 h-9 rounded-lg object-cover border border-gray-200"
                />
              ) : (
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7FD02E]/20 to-[#7FD02E]/5 flex items-center justify-center text-[#7FD02E] font-semibold">
                  {user.company.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          ) : user?.role === 'admin' ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Building className="w-4 h-4 text-[#7FD02E]" />
              <span>Vista plataforma · Sin empresa activa</span>
            </div>
          ) : null}
        </div>

        <div className="p-6 lg:p-8 flex-1">
          <Outlet />
        </div>
        {/* Footer */}
        <footer className="lg:pl-64 py-4 text-center text-sm text-gray-400 border-t border-gray-100">
          © NextPlan {new Date().getFullYear()}
        </footer>
      </main>
    </div>
  );
}

// User Menu Dropdown
const UserMenu = ({ user, onLogout }) => {
  const navigate = useNavigate();
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
          <ChevronRight className="w-4 h-4 lg:hidden text-gray-600" />
          <Settings className="w-4 h-4 hidden lg:block text-gray-600" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-white border-gray-200">
        <DropdownMenuLabel>
          <div className="flex items-center gap-3">
            <UserAvatar user={user} size="md" />
            <div>
              <p className="font-medium text-gray-800">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-gray-100" />
        <DropdownMenuItem 
          className="cursor-pointer text-gray-600 hover:text-gray-900" 
          data-testid="profile-menu-btn" 
          onClick={() => navigate('/profile')}
        >
          <User className="w-4 h-4 mr-2" />
          Mi Perfil
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer text-gray-600 hover:text-gray-900" data-testid="logout-btn" onClick={onLogout}>
          <LogOut className="w-4 h-4 mr-2" />
          Cerrar Sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
